<template>
    <ul
        class="relay-radio-status"
        data-test-id="relay-radio-status"
    >
        <li v-if="!hasIntegratedPower">
            <img
                :src="`/images/relays/${batteryLevelIcon}.svg`"
                :alt="batteryLevelDisplay"
                data-test-id="battery-charge-icon"
            />
            <span
                :class="{ warning: batteryLevelHasWarning }"
                data-test-id="battery-charge-label"
                >{{ batteryLevelDisplay }}</span
            >
        </li>
        <li>
            <img
                :src="`/images/relays/${batteryConnectedIcon}.svg`"
                :alt="batteryConnectedDisplay"
                data-test-id="battery-connected-icon"
            />
            <span
                :class="{ warning: batteryConnectedHasWarning }"
                data-test-id="battery-connected-label"
                >{{ batteryConnectedDisplay }}</span
            >
        </li>
        <li>
            <img
                :src="antennaQualityIcon"
                :alt="antennaQualityLabel"
                data-test-id="antenna-quality-icon"
            />
            <span
                :class="{ warning: antennaQualityHasWarning }"
                data-test-id="antenna-quality-label"
                >{{ antennaQualityLabel }}</span
            >
        </li>
    </ul>
</template>

<script>
import canToast from '@/mixins/canToast'
import needsOrganizationSlug from '@/mixins/needsOrganizationSlug'
import { ANTENNA_QUALITIES, findBatteryRange } from '../radio-status-definitions'

export default {
    mixins: [canToast, needsOrganizationSlug],
    props: ['row'],

    computed: {
        hasIntegratedPower() {
            return this.row?.latest_health_check?.serial_number?.startsWith('MNE')
        },
        antennaQualityHasWarning() {
            const antennaQuality = this.row?.latest_health_check?.antenna_quality?.toLowerCase()
            return (
                antennaQuality === ANTENNA_QUALITIES.POOR.toLowerCase() ||
                antennaQuality === ANTENNA_QUALITIES.BAD.toLowerCase()
            )
        },
        antennaQualityIcon() {
            let filename = `antenna-${ANTENNA_QUALITIES.UNKNOWN.toLowerCase()}.svg`
            if (this.row?.latest_health_check?.antenna_quality in ANTENNA_QUALITIES) {
                filename = `antenna-${this.row.latest_health_check.antenna_quality.toLowerCase()}.svg`
            }
            return `/images/relays/${filename}`
        },
        antennaQualityLabel() {
            let label = ANTENNA_QUALITIES.UNKNOWN
            if (this.row?.latest_health_check?.antenna_quality in ANTENNA_QUALITIES) {
                label = ANTENNA_QUALITIES[this.row.latest_health_check.antenna_quality]
            }
            return label
        },
        batteryLevelHasWarning() {
            const range = findBatteryRange(this.row?.latest_health_check?.battery_level)
            return range?.showWarning
        },
        batteryLevelIcon() {
            const range = findBatteryRange(this.row?.latest_health_check?.battery_level)
            return range ? range.icon : 'battery-unknown'
        },
        batteryLevelDisplay() {
            if (Number.isNaN(parseInt(this.row?.latest_health_check?.battery_level))) {
                return 'Unknown'
            }

            return `${this.row.latest_health_check.battery_level}%`
        },
        batteryChargingValue() {
            return this.row?.latest_health_check?.is_charging
        },
        batteryConnectedValue() {
            return this.row?.latest_health_check?.usb_connected
        },
        batteryStatusIsValid() {
            return (
                (this.batteryChargingValue === true || this.batteryChargingValue === false) &&
                (this.batteryConnectedValue === true || this.batteryConnectedValue === false)
            )
        },
        batteryConnectedHasWarning() {
            return this.batteryStatusIsValid && !this.batteryConnectedValue
        },
        batteryConnectedIcon() {
            if (!this.batteryStatusIsValid) {
                return 'battery-connection-unknown'
            }

            return this.batteryConnectedValue ? 'battery-connection-connected' : 'battery-connection-disconnected'
        },
        batteryConnectedDisplay() {
            if (!this.batteryStatusIsValid) {
                return 'Unknown'
            }
            if (this.batteryChargingValue) {
                return 'Charging'
            }

            if (this.batteryConnectedValue) {
                return 'Connected to Power'
            }

            return 'Not Charging'
        },
    },
}
</script>

<style scoped lang="scss">
.relay-radio-status {
    li {
        display: flex;
        align-items: center;

        &:not(:last-child) {
            margin-bottom: 10px;
        }

        span {
            margin-left: 10px;

            &.warning {
                color: #ffb3af;
            }
        }
    }
}
</style>
